/**
 * Super Admin Dashboard - Overview
 * Clean dashboard with statistics and quick access links
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

// react-big-calendar
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Chart components
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";

// API
import { superAdminAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

const localizer = momentLocalizer(moment);

function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reservationStats, setReservationStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({
    totalProperties: 0,
    approvedProperties: 0,
    pendingProperties: 0,
    inspectionProperties: 0,
    inspectionCompletedProperties: 0,
    rejectedProperties: 0,
    propertiesReadyForApproval: 0,
    totalPropertyOwners: 0,
    activePropertyOwners: 0,
    pendingPropertyOwners: 0,
    totalUsers: 0,
    activeUsers: 0,
    propertiesByType: {},
    propertiesByCity: {},
  });

  // Calendar state
  const [calendarView, setCalendarView] = useState("month");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Time range for trend charts
  const [timeRange, setTimeRange] = useState(12); // months

  useEffect(() => {
    fetchDashboardStats();
    fetchReservationData();
  }, []);

  const fetchReservationData = async () => {
    try {
      const [statsResponse, reservationsResponse] = await Promise.all([
        superAdminAPI.getReservationStatistics().catch(() => ({ data: null })),
        superAdminAPI.getAllReservations({ limit: 10000 }).catch(() => ({ data: [] })),
      ]);

      if (statsResponse.data) {
        setReservationStats(statsResponse.data);
      }

      const reservationsList = reservationsResponse.data || [];
      setReservations(reservationsList);
    } catch (error) {
      console.error("Error fetching reservation data:", error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      console.log("🔵 Super Admin Dashboard - Fetching stats...");

      const [properties, propertyOwners, users, propertiesReady] = await Promise.all([
        superAdminAPI.getAllProperties({ limit: 10000 }), // Get all properties without pagination
        superAdminAPI.getAllPropertyOwners(),
        superAdminAPI.getAllUsers({ limit: 10000 }), // Get all users without pagination
        superAdminAPI.getPropertiesReadyForApproval(),
      ]);

      console.log("🔵 Super Admin Dashboard - API Responses:", {
        properties: properties,
        propertyOwners: propertyOwners,
        users: users,
        propertiesReady: propertiesReady,
      });

      // Handle different response structures - try multiple possible formats
      let propertiesList = [];
      if (Array.isArray(properties)) {
        propertiesList = properties;
      } else if (properties?.data && Array.isArray(properties.data)) {
        propertiesList = properties.data;
      } else if (properties?.properties && Array.isArray(properties.properties)) {
        propertiesList = properties.properties;
      }

      let ownersList = [];
      if (Array.isArray(propertyOwners)) {
        ownersList = propertyOwners;
      } else if (propertyOwners?.data && Array.isArray(propertyOwners.data)) {
        ownersList = propertyOwners.data;
      } else if (propertyOwners?.owners && Array.isArray(propertyOwners.owners)) {
        ownersList = propertyOwners.owners;
      }

      let usersList = [];
      if (Array.isArray(users)) {
        usersList = users;
      } else if (users?.data && Array.isArray(users.data)) {
        usersList = users.data;
      } else if (users?.users && Array.isArray(users.users)) {
        usersList = users.users;
      }

      let readyPropertiesList = [];
      if (Array.isArray(propertiesReady)) {
        readyPropertiesList = propertiesReady;
      } else if (propertiesReady?.data && Array.isArray(propertiesReady.data)) {
        readyPropertiesList = propertiesReady.data;
      } else if (propertiesReady?.properties && Array.isArray(propertiesReady.properties)) {
        readyPropertiesList = propertiesReady.properties;
      }

      console.log("🔵 Super Admin Dashboard - Extracted Lists:", {
        propertiesList: propertiesList.length,
        ownersList: ownersList.length,
        usersList: usersList.length,
        readyPropertiesList: readyPropertiesList.length,
      });

      console.log("🔵 Super Admin Dashboard - Processed Data:", {
        propertiesList: propertiesList.length,
        ownersList: ownersList.length,
        usersList: usersList.length,
        readyPropertiesList: readyPropertiesList.length,
      });

      // Calculate statistics
      const totalProperties = propertiesList.length;
      const approvedProperties = propertiesList.filter((p) => p.status === "Approved").length;
      const pendingProperties = propertiesList.filter((p) => p.status === "Pending").length;
      const inspectionProperties = propertiesList.filter((p) => p.status === "Inspection").length;
      const inspectionCompletedProperties = propertiesList.filter(
        (p) => p.status === "Inspection_Completed"
      ).length;
      const rejectedProperties = propertiesList.filter((p) => p.status === "Rejected").length;
      const propertiesReadyForApproval = readyPropertiesList.length;

      const totalPropertyOwners = ownersList.length;
      const activePropertyOwners = ownersList.filter((po) => po.status === "Active").length;
      const pendingPropertyOwners = ownersList.filter((po) => po.status === "Pending").length;

      const totalUsers = usersList.length;
      const activeUsers = usersList.filter((u) => u.isActive !== false).length;

      // Properties by type
      const propertiesByType = {};
      propertiesList.forEach((p) => {
        const type = p.propertyType || "Unknown";
        propertiesByType[type] = (propertiesByType[type] || 0) + 1;
      });

      // Properties by city (use English label for display)
      const propertiesByCity = {};
      propertiesList.forEach((p) => {
        const city =
          typeof p.city === "object" && p.city != null
            ? p.city.en || p.city.ar || "Unknown"
            : p.city || "Unknown";
        propertiesByCity[city] = (propertiesByCity[city] || 0) + 1;
      });

      setStats({
        totalProperties,
        approvedProperties,
        pendingProperties,
        inspectionProperties,
        inspectionCompletedProperties,
        rejectedProperties,
        propertiesReadyForApproval,
        totalPropertyOwners,
        activePropertyOwners,
        pendingPropertyOwners,
        totalUsers,
        activeUsers,
        propertiesByType,
        propertiesByCity,
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePercentage = (part, total) => {
    if (total > 0) {
      return Math.round((part / total) * 100);
    }
    return 0;
  };

  // Prepare chart data
  const propertyStatusData = {
    labels: ["Approved", "Pending", "Inspection", "Rejected"],
    datasets: {
      label: "Properties",
      backgroundColors: ["success", "warning", "info", "error"],
      data: [
        stats.approvedProperties,
        stats.pendingProperties,
        stats.inspectionProperties + stats.inspectionCompletedProperties,
        stats.rejectedProperties,
      ],
    },
  };

  const propertyTypeData = {
    labels: Object.keys(stats.propertiesByType),
    datasets: {
      label: "Properties by Type",
      backgroundColors: ["info", "primary", "success", "warning", "error", "dark"],
      data: Object.values(stats.propertiesByType),
    },
  };

  const topCitiesData = {
    labels: Object.keys(stats.propertiesByCity)
      .sort((a, b) => stats.propertiesByCity[b] - stats.propertiesByCity[a])
      .slice(0, 5),
    datasets: [
      {
        label: "Properties",
        color: "info",
        data: Object.keys(stats.propertiesByCity)
          .sort((a, b) => stats.propertiesByCity[b] - stats.propertiesByCity[a])
          .slice(0, 5)
          .map((city) => stats.propertiesByCity[city]),
      },
    ],
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Helper function to extract multilingual text
  const getMultilingualText = (field, defaultText = "N/A") => {
    if (!field) return defaultText;
    if (typeof field === "string") return field;
    if (typeof field === "object") {
      // Handle multilingual object {en: "...", ar: "..."}
      return field.en || field.ar || defaultText;
    }
    return defaultText;
  };

  // Calendar events
  const calendarEvents = useMemo(() => {
    return reservations
      .filter((reservation) => reservation.checkInDate && reservation.checkOutDate)
      .map((reservation) => {
        const start = moment(reservation.checkInDate).toDate();
        const end = moment(reservation.checkOutDate).endOf("day").toDate();

        let statusColor = "#3174ad";
        if (reservation.status === "CheckedIn") statusColor = "#28a745";
        else if (reservation.status === "CheckedOut") statusColor = "#6c757d";
        else if (reservation.status === "Cancelled" || reservation.status === "Refunded")
          statusColor = "#dc3545";

        const propertyTitle = getMultilingualText(reservation.property?.title, "Property");
        const clientEmail = reservation.client?.email || "Guest";

        return {
          id: reservation._id,
          title: `${propertyTitle} - ${clientEmail}`,
          start,
          end,
          resource: reservation,
          status: reservation.status,
          statusColor,
        };
      });
  }, [reservations]);

  // Handle calendar navigation
  const handleNavigate = (newDate) => {
    setCalendarDate(newDate);
  };

  const handleViewChange = (newView) => {
    setCalendarView(newView);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  // Prepare trend chart data with time range filter
  const getFilteredTrendData = (labels, data, label, color) => {
    if (!reservationStats?.monthlyTrends || !labels || !data) return null;

    // Filter based on timeRange
    const filteredLabels = labels.slice(-timeRange);
    const filteredData = data.slice(-timeRange);

    return {
      labels: filteredLabels.map((labelStr) => {
        const [year, month] = labelStr.split("-");
        return moment(`${year}-${month}-01`).format("MMM YY");
      }),
      datasets: {
        label: label,
        color: color,
        data: filteredData,
      },
    };
  };

  // Prepare revenue trend chart data
  const revenueTrendData = reservationStats?.monthlyTrends
    ? getFilteredTrendData(
        reservationStats.monthlyTrends.labels,
        reservationStats.monthlyTrends.revenue,
        "Revenue",
        "success"
      )
    : null;

  // Prepare booking trend chart data
  const bookingTrendData = reservationStats?.monthlyTrends
    ? getFilteredTrendData(
        reservationStats.monthlyTrends.labels,
        reservationStats.monthlyTrends.bookings,
        "Bookings",
        "info"
      )
    : null;

  // Prepare platform fees trend chart data
  const platformFeesTrendData = reservationStats?.monthlyTrends
    ? getFilteredTrendData(
        reservationStats.monthlyTrends.labels,
        reservationStats.monthlyTrends.platformFees,
        "Platform Fees",
        "warning"
      )
    : null;

  // Status breakdown chart data
  const statusBreakdownData = reservationStats?.statusBreakdown
    ? {
        labels: Object.keys(reservationStats.statusBreakdown),
        datasets: {
          label: "Reservations",
          backgroundColors: ["info", "success", "secondary", "error", "error", "warning"],
          data: Object.values(reservationStats.statusBreakdown),
        },
      }
    : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              Dashboard Overview
            </MDTypography>
            <MDTypography variant="body2" color="text">
              System statistics and quick access to management pages
            </MDTypography>
          </MDBox>
        </MDBox>

        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </MDBox>
        ) : (
          <>
            {/* Tabs for Dashboard Sections */}
            <MDBox mb={3}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Overview" />
                <Tab label="Reservations & Revenue" />
              </Tabs>
            </MDBox>

            {tabValue === 0 && (
              <>
                {/* Primary Statistics */}
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <ComplexStatisticsCard
                      color="dark"
                      icon="home"
                      title="Total Properties"
                      count={stats.totalProperties}
                      percentage={{
                        color: "success",
                        amount: "",
                        label: "All properties",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <ComplexStatisticsCard
                      color="success"
                      icon="people"
                      title="Property Owners"
                      count={stats.totalPropertyOwners}
                      percentage={{
                        color: "success",
                        amount: `${stats.pendingPropertyOwners} pending`,
                        label: "awaiting approval",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <ComplexStatisticsCard
                      color="info"
                      icon="person"
                      title="System Users"
                      count={stats.totalUsers}
                      percentage={{
                        color: "success",
                        amount: `${stats.activeUsers} active`,
                        label: "users",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <ComplexStatisticsCard
                      color="warning"
                      icon="task_alt"
                      title="Ready for Approval"
                      count={stats.propertiesReadyForApproval}
                      percentage={{
                        color: "warning",
                        amount: "Inspection completed",
                        label: "awaiting review",
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {tabValue === 1 && (
              <>
                {/* Reservation Statistics Cards */}
                {reservationStats && (
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <ComplexStatisticsCard
                        color="info"
                        icon="book_online"
                        title="Total Bookings"
                        count={reservationStats.overview?.totalBookings || 0}
                        percentage={{
                          color: "success",
                          amount: `${reservationStats.overview?.occupancyRate || 0}%`,
                          label: "occupancy rate",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <ComplexStatisticsCard
                        color="success"
                        icon="payments"
                        title="Total Revenue"
                        count={formatCurrency(
                          (reservationStats.overview?.totalRevenue || 0) +
                            (reservationStats.overview?.totalPlatformFees || 0) +
                            (reservationStats.overview?.totalInsuranceDeposits || 0)
                        )}
                        percentage={{
                          color: "success",
                          amount: formatCurrency(
                            reservationStats.overview?.averageBookingValue || 0
                          ),
                          label: "avg per booking",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <ComplexStatisticsCard
                        color="warning"
                        icon="account_balance"
                        title="Platform Revenue"
                        count={formatCurrency(reservationStats.overview?.totalPlatformFees || 0)}
                        percentage={{
                          color: "warning",
                          amount: formatCurrency(
                            reservationStats.overview?.averagePlatformFee || 0
                          ),
                          label: "avg per booking",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <ComplexStatisticsCard
                        color="dark"
                        icon="security"
                        title="Insurance Deposits"
                        count={formatCurrency(
                          reservationStats.overview?.totalInsuranceDeposits || 0
                        )}
                        percentage={{
                          color: "info",
                          amount: "",
                          label: "total held",
                        }}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Revenue and Booking Trends */}
                {revenueTrendData && bookingTrendData && (
                  <Grid container spacing={3} mb={3} mt={3}>
                    <Grid item xs={12}>
                      <MDBox display="flex" justifyContent="flex-end" mb={2}>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel>Time Range</InputLabel>
                          <Select
                            value={timeRange}
                            label="Time Range"
                            onChange={(e) => setTimeRange(e.target.value)}
                          >
                            <MenuItem value={3}>Last 3 Months</MenuItem>
                            <MenuItem value={6}>Last 6 Months</MenuItem>
                            <MenuItem value={12}>Last 12 Months</MenuItem>
                          </Select>
                        </FormControl>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <MDBox p={3}>
                          <MDTypography variant="h6" fontWeight="bold" mb={2}>
                            Revenue Trend
                          </MDTypography>
                          <MDBox mt={2}>
                            <ReportsLineChart
                              icon={{ color: "success", component: "trending_up" }}
                              title="Monthly Revenue"
                              description="Total revenue over time"
                              chart={revenueTrendData}
                            />
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <MDBox p={3}>
                          <MDTypography variant="h6" fontWeight="bold" mb={2}>
                            Booking Trend
                          </MDTypography>
                          <MDBox mt={2}>
                            <ReportsLineChart
                              icon={{ color: "info", component: "calendar_today" }}
                              title="Monthly Bookings"
                              description="Number of bookings over time"
                              chart={bookingTrendData}
                            />
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* Platform Fees Trend and Status Breakdown */}
                {platformFeesTrendData && statusBreakdownData && (
                  <Grid container spacing={3} mb={3} mt={3}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <MDBox p={3}>
                          <MDTypography variant="h6" fontWeight="bold" mb={2}>
                            Platform Fees Trend
                          </MDTypography>
                          <MDBox mt={2}>
                            <ReportsLineChart
                              icon={{ color: "warning", component: "account_balance" }}
                              title="Monthly Platform Fees"
                              description="Platform revenue over time"
                              chart={platformFeesTrendData}
                            />
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <MDBox p={3}>
                          <MDTypography variant="h6" fontWeight="bold" mb={2}>
                            Reservation Status Distribution
                          </MDTypography>
                          <MDBox mt={2}>
                            <DefaultDoughnutChart
                              icon={{ color: "info", component: "pie_chart" }}
                              title="Status Breakdown"
                              description="Distribution of reservation statuses"
                              chart={statusBreakdownData}
                            />
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* Calendar View */}
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12}>
                    <Card>
                      <MDBox p={3}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <MDTypography variant="h6" fontWeight="bold">
                            Reservations Calendar
                          </MDTypography>
                          <MDBox display="flex" gap={1} alignItems="center">
                            <ButtonGroup size="small">
                              <Button
                                variant={calendarView === "month" ? "contained" : "outlined"}
                                onClick={() => handleViewChange("month")}
                              >
                                Month
                              </Button>
                              <Button
                                variant={calendarView === "week" ? "contained" : "outlined"}
                                onClick={() => handleViewChange("week")}
                              >
                                Week
                              </Button>
                              <Button
                                variant={calendarView === "day" ? "contained" : "outlined"}
                                onClick={() => handleViewChange("day")}
                              >
                                Day
                              </Button>
                            </ButtonGroup>
                            <MDButton
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setCalendarDate(new Date());
                                handleViewChange("month");
                              }}
                              sx={{ ml: 1 }}
                            >
                              Today
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                handleNavigate(
                                  moment(calendarDate).subtract(1, calendarView).toDate()
                                )
                              }
                              sx={{ ml: 1 }}
                            >
                              <Icon>chevron_left</Icon>
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                handleNavigate(moment(calendarDate).add(1, calendarView).toDate())
                              }
                            >
                              <Icon>chevron_right</Icon>
                            </MDButton>
                          </MDBox>
                        </MDBox>

                        {/* Status Legend */}
                        <MDBox display="flex" gap={2} mb={2} flexWrap="wrap">
                          <Chip
                            label="Confirmed"
                            size="small"
                            sx={{ bgcolor: "#3174ad", color: "white" }}
                          />
                          <Chip
                            label="Checked In"
                            size="small"
                            sx={{ bgcolor: "#28a745", color: "white" }}
                          />
                          <Chip
                            label="Checked Out"
                            size="small"
                            sx={{ bgcolor: "#6c757d", color: "white" }}
                          />
                          <Chip
                            label="Cancelled"
                            size="small"
                            sx={{ bgcolor: "#dc3545", color: "white" }}
                          />
                        </MDBox>

                        <MDBox height="600px" mt={2}>
                          {calendarEvents.length > 0 ? (
                            <Calendar
                              localizer={localizer}
                              events={calendarEvents}
                              startAccessor="start"
                              endAccessor="end"
                              view={calendarView}
                              date={calendarDate}
                              onNavigate={handleNavigate}
                              onView={handleViewChange}
                              onSelectEvent={handleSelectEvent}
                              eventPropGetter={(event) => ({
                                style: {
                                  backgroundColor: event.statusColor,
                                  borderColor: event.statusColor,
                                  color: "white",
                                  borderRadius: "4px",
                                  border: "none",
                                  padding: "2px 4px",
                                  fontSize: "12px",
                                },
                              })}
                              style={{ height: "100%" }}
                              popup
                              showMultiDayTimes
                            />
                          ) : (
                            <MDBox
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              height="100%"
                              flexDirection="column"
                            >
                              <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>
                                event_busy
                              </Icon>
                              <MDTypography variant="h6" color="text.secondary">
                                No reservations found
                              </MDTypography>
                            </MDBox>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Property Status Overview - Only show in Overview tab */}
            {tabValue === 0 && (
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <MDBox p={3}>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        Property Status Distribution
                      </MDTypography>
                      <DefaultDoughnutChart
                        icon={{ color: "info", component: "home" }}
                        title="Properties by Status"
                        description="Current status of all properties"
                        chart={propertyStatusData}
                      />
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <MDBox p={3}>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        Properties by Type
                      </MDTypography>
                      <DefaultDoughnutChart
                        icon={{ color: "info", component: "category" }}
                        title="Property Types"
                        description="Distribution of property types"
                        chart={propertyTypeData}
                      />
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Top Cities Chart - Only show in Overview tab */}
            {tabValue === 0 &&
              Object.keys(stats.propertiesByCity).length > 0 &&
              topCitiesData.labels.length > 0 && (
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12}>
                    <Card>
                      <MDBox p={3}>
                        <MDTypography variant="h6" fontWeight="bold" mb={2}>
                          Top Cities by Property Count
                        </MDTypography>
                        <VerticalBarChart
                          icon={{ color: "info", component: "location_on" }}
                          title="Properties by City"
                          description="Top 5 cities with most properties"
                          chart={topCitiesData}
                        />
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              )}

            {/* Quick Access Cards - Only show in Overview tab */}
            {tabValue === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDTypography variant="h5" fontWeight="bold" mb={2}>
                    Quick Access
                  </MDTypography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => navigate("/super-admin/property-owners")}
                  >
                    <MDBox p={3} textAlign="center">
                      <MDBox
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        width="64px"
                        height="64px"
                        borderRadius="50%"
                        bgColor="success.main"
                        mb={2}
                      >
                        <Icon sx={{ fontSize: "2rem", color: "white" }}>people</Icon>
                      </MDBox>
                      <MDTypography variant="h6" fontWeight="bold" mb={1}>
                        Property Owners
                      </MDTypography>
                      <MDTypography variant="body2" color="text" mb={2}>
                        Manage property owners, approve accounts, and view details
                      </MDTypography>
                      <MDButton variant="gradient" color="success" fullWidth>
                        Manage Owners
                      </MDButton>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => navigate("/super-admin/properties")}
                  >
                    <MDBox p={3} textAlign="center">
                      <MDBox
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        width="64px"
                        height="64px"
                        borderRadius="50%"
                        bgColor="info.main"
                        mb={2}
                      >
                        <Icon sx={{ fontSize: "2rem", color: "white" }}>home</Icon>
                      </MDBox>
                      <MDTypography variant="h6" fontWeight="bold" mb={1}>
                        Properties
                      </MDTypography>
                      <MDTypography variant="body2" color="text" mb={2}>
                        View all properties, set prices, and manage listings
                      </MDTypography>
                      <MDButton variant="gradient" color="info" fullWidth>
                        Manage Properties
                      </MDButton>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => navigate("/super-admin/users")}
                  >
                    <MDBox p={3} textAlign="center">
                      <MDBox
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        width="64px"
                        height="64px"
                        borderRadius="50%"
                        bgColor="warning.main"
                        mb={2}
                      >
                        <Icon sx={{ fontSize: "2rem", color: "white" }}>person</Icon>
                      </MDBox>
                      <MDTypography variant="h6" fontWeight="bold" mb={1}>
                        Users Management
                      </MDTypography>
                      <MDTypography variant="body2" color="text" mb={2}>
                        Create and manage user accounts for all roles
                      </MDTypography>
                      <MDButton variant="gradient" color="warning" fullWidth>
                        Manage Users
                      </MDButton>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => navigate("/super-admin/inspections")}
                  >
                    <MDBox p={3} textAlign="center">
                      <MDBox
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        width="64px"
                        height="64px"
                        borderRadius="50%"
                        bgColor="error.main"
                        mb={2}
                      >
                        <Icon sx={{ fontSize: "2rem", color: "white" }}>task_alt</Icon>
                      </MDBox>
                      <MDTypography variant="h6" fontWeight="bold" mb={1}>
                        Inspections
                      </MDTypography>
                      <MDTypography variant="body2" color="text" mb={2}>
                        Review completed inspections and approve/reject properties
                      </MDTypography>
                      <MDButton variant="gradient" color="error" fullWidth>
                        Review Inspections
                      </MDButton>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            )}
          </>
        )}

        {/* Reservation Details Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6" fontWeight="bold">
                Reservation Details
              </MDTypography>
              <IconButton onClick={() => setDialogOpen(false)} size="small">
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            {selectedEvent && selectedEvent.resource && (
              <MDBox>
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Property
                  </MDTypography>
                  <MDTypography variant="body1" fontWeight="bold">
                    {getMultilingualText(selectedEvent.resource.property?.title, "N/A")}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Client
                  </MDTypography>
                  <MDTypography variant="body1">
                    {selectedEvent.resource.client?.email || "N/A"}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Check-In Date
                  </MDTypography>
                  <MDTypography variant="body1">
                    {moment(selectedEvent.resource.checkInDate).format("MMM DD, YYYY")}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Check-Out Date
                  </MDTypography>
                  <MDTypography variant="body1">
                    {moment(selectedEvent.resource.checkOutDate).format("MMM DD, YYYY")}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Status
                  </MDTypography>
                  <Chip
                    label={selectedEvent.resource.status || "N/A"}
                    size="small"
                    sx={{
                      bgcolor: selectedEvent.statusColor,
                      color: "white",
                    }}
                  />
                </MDBox>

                <Divider sx={{ my: 2 }} />

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Total Amount
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="bold" color="success.main">
                    {formatCurrency(selectedEvent.resource.pricing?.totalAmount || 0)}
                  </MDTypography>
                </MDBox>

                {selectedEvent.resource.property?.profitPercentage && (
                  <MDBox mb={2}>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="medium"
                      display="block"
                      mb={0.5}
                    >
                      Platform Fee
                    </MDTypography>
                    <MDTypography variant="body1" fontWeight="bold" color="warning.main">
                      {formatCurrency(
                        ((selectedEvent.resource.pricing?.totalAmount || 0) *
                          (selectedEvent.resource.property?.profitPercentage || 0)) /
                          100
                      )}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton onClick={() => setDialogOpen(false)}>Close</MDButton>
            {selectedEvent && (
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  setDialogOpen(false);
                  navigate(`/super-admin/reservations/${selectedEvent.id}`);
                }}
              >
                View Full Details
              </MDButton>
            )}
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SuperAdminDashboard;
